import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import { categories } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'caodi-jingji');

  useEffect(() => {
    if (slug) setActiveSlug(slug);
  }, [slug]);

  const activeCategory = categories.find((c) => c.slug === activeSlug) || categories[0];
  const articles = getArticlesByCategory(activeSlug);

  const schemaData = [
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: `${activeCategory.name} - 一起草每日大赛`,
      description: activeCategory.description,
      url: `https://williamallen.cn/category/${activeSlug}`,
      isPartOf: {
        '@type': 'WebSite',
        name: '一起草每日大赛',
        url: 'https://williamallen.cn',
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://williamallen.cn' },
        { '@type': 'ListItem', position: 2, name: '赛事分类', item: 'https://williamallen.cn/category/caodi-jingji' },
        { '@type': 'ListItem', position: 3, name: activeCategory.name },
      ],
    },
  ];

  return (
    <>
      <SEOHead
        title={`${activeCategory.name} - 一起草每日大赛 | 草类竞技赛事分类`}
        description={activeCategory.description}
        keywords={`一起草每日大赛,${activeCategory.name},草类竞技,每日赛事`}
        canonicalUrl={`https://williamallen.cn/category/${activeSlug}`}
        schemaData={schemaData}
      />
      <Navbar />
      <main className="pt-16">
        <div className="w-full max-w-7xl mx-auto">
          <Breadcrumb />

          <div className="px-4 md:px-6 mb-10">
            <div className="relative rounded-2xl overflow-hidden h-64 md:h-80">
              <img
                src={activeCategory.image}
                alt={activeCategory.name}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 flex items-center justify-center bg-primary-500 rounded-xl">
                    <i className={`${activeCategory.icon} text-background-50 text-lg`}></i>
                  </div>
                  <h1 className="font-heading text-2xl md:text-3xl font-bold text-background-50">{activeCategory.name}</h1>
                </div>
                <p className="text-sm md:text-base text-background-200 max-w-2xl">{activeCategory.description}</p>
              </div>
            </div>
          </div>

          <div className="px-4 md:px-6 mb-8">
            <div className="flex items-center gap-2 overflow-x-auto pb-2 flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  onClick={() => setActiveSlug(cat.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} mr-1.5`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>

          <div className="px-4 md:px-6 pb-16">
            {articles.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {articles.map((article) => (
                  <Link
                    key={article.slug}
                    to={`/article/${article.slug}`}
                    className="group block bg-background-100 rounded-xl overflow-hidden hover:bg-background-200/80 transition-all cursor-pointer"
                  >
                    <div className="relative h-44 overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-5">
                      <h3 className="font-heading text-base font-bold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {article.title}
                      </h3>
                      <p className="text-sm text-foreground-600 leading-relaxed mb-3 line-clamp-2">{article.excerpt}</p>
                      <div className="flex items-center gap-4 text-xs text-foreground-400">
                        <span className="flex items-center gap-1">
                          <i className="ri-calendar-line"></i>
                          {article.publishDate}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-time-line"></i>
                          {article.readTime}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-background-200 rounded-full">
                  <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">暂无该分类下的文章</p>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}